import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Lock,
  Shield,
  Bell,
  Eye,
  EyeOff,
  CheckSquare,
  Square,
} from "lucide-react";
import { authAPI } from "../../services/api";

export default function PrivacySecuritySettings() {
  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: "public",
    show_location: true,
    show_email: true,
    show_phone: true,
    two_factor_authentication: false,
    login_notifications: true,
    data_encryption: true,
  });

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Fetch privacy settings on component mount
  useEffect(() => {
    fetchPrivacySettings();
  }, []);

  const fetchPrivacySettings = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getPrivacySettings();
      setPrivacySettings(response.data);
    } catch (error) {
      setError("Failed to load privacy settings");
      showErrorAlertMessage("Failed to load privacy settings");
    } finally {
      setLoading(false);
    }
  };

  // Toggle boolean settings
  const toggleSetting = (settingName) => {
    setPrivacySettings({
      ...privacySettings,
      [settingName]: !privacySettings[settingName],
    });
  };

  // Handle profile visibility change
  const handleVisibilityChange = (e) => {
    setPrivacySettings({
      ...privacySettings,
      profile_visibility: e.target.value,
    });
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
    // Clear any previous messages when user starts typing
    setErrorMessage("");
    setSuccessMessage("");
  };

  // Submit password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      showErrorAlertMessage("All password fields are required");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorAlertMessage("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showErrorAlertMessage("Password must be at least 8 characters long");
      return;
    }

    try {
      setSavingPassword(true);
      await authAPI.changeUserPassword(
        passwordData.currentPassword,
        passwordData.newPassword,
        passwordData.confirmPassword
      );
      showSuccessAlertMessage("Password changed successfully");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to change password. Please try again.";
      showErrorAlertMessage(errorMessage);
    } finally {
      setSavingPassword(false);
    }
  };

  // Show success alert
  const showSuccessAlertMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage("");
    }, 4000);
  };

  // Show error alert
  const showErrorAlertMessage = (message) => {
    setErrorMessage(message);
    setShowErrorAlert(true);
    setTimeout(() => {
      setShowErrorAlert(false);
      setErrorMessage("");
    }, 4000);
  };

  // Save all privacy and security settings
  const saveAllSettings = async () => {
    try {
      setSavingPrivacy(true);
      await authAPI.updatePrivacySettings(privacySettings);
      showSuccessAlertMessage("Privacy settings updated successfully");
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to save settings. Please try again.";
      showErrorAlertMessage(errorMessage);
    } finally {
      setSavingPrivacy(false);
    }
  };

  // Render checkbox setting
  const renderCheckboxSetting = (settingKey, label, description) => {
    const isChecked = privacySettings[settingKey];

    return (
      <div className="setting-item-apss">
        <div className="setting-details-apss">
          <div className="setting-label-apss">{label}</div>
          <div className="setting-description-apss">{description}</div>
        </div>
        <button
          className="toggle-button-apss"
          onClick={() => toggleSetting(settingKey)}
          disabled={savingPrivacy}
        >
          {isChecked ? (
            <CheckSquare color={"#0e4410"} size={20} />
          ) : (
            <Square color={"#0e4410"} size={20} />
          )}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="privacy-security-container-apss">
        <div className="loading-container-apss">
          <div className="loading-spinner-apss"></div>
          <p>Loading privacy settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="privacy-security-container-apss">
        <div className="error-container-apss">
          <p className="error-message-apss">{error}</p>
          <button onClick={fetchPrivacySettings} className="retry-button-apss">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-security-container-apss">
      <div className="privacy-security-header-apss">
        <button
          className="back-button-apss"
          onClick={() => window.history.back()}
        >
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="privacy-security-title-apss">Privacy & Security</h1>
      </div>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-apss">
          <div className="success-alert-content-apss">
            <div className="success-alert-icon-apss">✓</div>
            <div className="success-alert-message-apss">{successMessage}</div>
            <button 
              className="success-alert-close-apss"
              onClick={() => setShowSuccessAlert(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <div className="error-alert-apss">
          <div className="error-alert-content-apss">
            <div className="error-alert-icon-apss">!</div>
            <div className="error-alert-message-apss">{errorMessage}</div>
            <button 
              className="error-alert-close-apss"
              onClick={() => setShowErrorAlert(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Privacy Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Eye size={24} />
          <h3 className="section-title-apss">Privacy Settings</h3>
        </div>
        <div className="section-content-apss">
          <div className="setting-item-apss">
            <div className="setting-details-apss">
              <div className="setting-label-apss">Profile Visibility</div>
              <div className="setting-description-apss">
                Control who can see your profile information
              </div>
            </div>
            <select
              className="visibility-select-apss"
              value={privacySettings.profile_visibility}
              onChange={handleVisibilityChange}
              disabled={savingPrivacy}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>

          {renderCheckboxSetting(
            "show_location",
            "Show Location",
            "Display your location on your profile"
          )}

          {renderCheckboxSetting(
            "show_email",
            "Show Email",
            "Make your email visible to others"
          )}

          {renderCheckboxSetting(
            "show_phone",
            "Show Phone Number",
            "Make your phone number visible to others"
          )}
        </div>
      </div>

      {/* Security Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Shield size={24} />
          <h3 className="section-title-apss">Security Settings</h3>
        </div>
        <div className="section-content-apss">
          {renderCheckboxSetting(
            "two_factor_authentication",
            "Two-Factor Authentication",
            "Add an extra layer of security to your account"
          )}

          {renderCheckboxSetting(
            "login_notifications",
            "Login Notifications",
            "Get notified of new login activities"
          )}

          {renderCheckboxSetting(
            "data_encryption",
            "Data Encryption",
            "Enable end-to-end encryption for your data"
          )}
        </div>
      </div>

      {/* Save Privacy Settings Button */}
      <div className="save-changes-container-apss">
        <button
          className="save-all-changes-button-apss"
          onClick={saveAllSettings}
          disabled={savingPrivacy}
        >
          {savingPrivacy ? 'Saving...' : 'Save Privacy Settings'}
        </button>
      </div>

      {/* Password Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Lock size={24} />
          <h3 className="section-title-apss">Change Password</h3>
        </div>
        <div className="section-content-apss">
          <form className="password-form-apss" onSubmit={handlePasswordSubmit}>
            <div className="password-field-apss">
              <label className="password-label-apss">Current Password</label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="password-input-apss"
                disabled={savingPassword}
              />
            </div>
            <div className="password-field-apss">
              <label className="password-label-apss">New Password</label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="password-input-apss"
                disabled={savingPassword}
              />
            </div>
            <div className="password-field-apss">
              <label className="password-label-apss">
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="password-input-apss"
                disabled={savingPassword}
              />
            </div>
            <button 
              type="submit" 
              className="change-password-button-apss"
              disabled={savingPassword}
            >
              {savingPassword ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>

      <style jsx>{`
        /* Alert Styles */
        .success-alert-apss, .error-alert-apss {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          animation: slideInRight 0.3s ease-out;
        }

        .success-alert-content-apss, .error-alert-content-apss {
          border-radius: 8px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 300px;
        }

        .success-alert-content-apss {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
        }

        .error-alert-content-apss {
          background: #fef2f2;
          border: 1px solid #fecaca;
        }

        .success-alert-icon-apss {
          background: #22c55e;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .error-alert-icon-apss {
          background: #ef4444;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .success-alert-message-apss {
          color: #166534;
          font-weight: 500;
          flex: 1;
        }

        .error-alert-message-apss {
          color: #dc2626;
          font-weight: 500;
          flex: 1;
        }

        .success-alert-close-apss, .error-alert-close-apss {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .success-alert-close-apss {
          color: #166534;
        }

        .error-alert-close-apss {
          color: #dc2626;
        }

        .success-alert-close-apss:hover {
          color: #0f5132;
        }

        .error-alert-close-apss:hover {
          color: #b91c1c;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Disabled Button Styles */
        .change-password-button-apss:disabled,
        .save-all-changes-button-apss:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .password-input-apss:disabled {
          background: #f9fafb;
          cursor: not-allowed;
        }

        .visibility-select-apss:disabled {
          background: #f9fafb;
          cursor: not-allowed;
        }

        .toggle-button-apss:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Loading and Error States */
        .loading-container-apss {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 20px;
        }

        .loading-spinner-apss {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #0e4410;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-container-apss {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 20px;
        }

        .error-message-apss {
          color: #dc2626;
          font-size: 16px;
          text-align: center;
        }

        .retry-button-apss {
          background: #0e4410;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .retry-button-apss:hover {
          background: #0a2e08;
        }
      `}</style>
    </div>
  );
}
